import { uid } from '../lib/id';
import type { Asset } from '../storage/schemas';
import { sha256Hex, sniffImageMime } from './bytes';

export const MAX_IMAGE_INPUT_BYTES = 25 * 1024 * 1024;
const MAX_EDGE = 1600;

export class ImageImportError extends Error {}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function canvasToBytes(c: HTMLCanvasElement, type: string, quality?: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) =>
    c.toBlob((b) => (b ? b.arrayBuffer().then(resolve, reject) : reject(new ImageImportError('Could not encode image.'))), type, quality),
  );
}

async function decode(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(blob);
    } catch {
      /* fall through to <img> (e.g. older Safari with some formats) */
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } catch {
    throw new ImageImportError('This image format is not supported by your browser.');
  } finally {
    URL.revokeObjectURL(url);
  }
}

function hasTransparency(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  const data = ctx.getImageData(0, 0, w, h).data;
  for (let i = 3; i < data.length; i += 16) if (data[i] < 250) return true;
  return false;
}

/** Validate, decode, downscale and re-encode an image picked by the user. Strips metadata (EXIF/GPS) as a side effect. */
export async function importImage(file: Blob, name?: string): Promise<Asset> {
  if (file.size > MAX_IMAGE_INPUT_BYTES) throw new ImageImportError('Images must be smaller than 25 MB.');
  const head = new Uint8Array(await file.slice(0, 32).arrayBuffer());
  const mime = sniffImageMime(head);
  if (!mime) throw new ImageImportError('That file is not a supported image (PNG, JPEG, GIF, WebP, AVIF or HEIC).');
  const bmp = await decode(file);
  const sw = 'naturalWidth' in bmp ? bmp.naturalWidth : bmp.width;
  const sh = 'naturalHeight' in bmp ? bmp.naturalHeight : bmp.height;
  if (!sw || !sh) throw new ImageImportError('The image appears to be empty.');
  const scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  const c = makeCanvas(w, h);
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new ImageImportError('Canvas is unavailable.');
  ctx.drawImage(bmp, 0, 0, w, h);
  if ('close' in bmp) bmp.close();
  const alpha = mime === 'image/png' || mime === 'image/webp' || mime === 'image/gif' ? hasTransparency(ctx, w, h) : false;
  const outType = alpha ? 'image/png' : 'image/jpeg';
  const data = await canvasToBytes(c, outType, 0.88);
  return {
    id: uid('a'),
    kind: 'image',
    mime: outType,
    name: name?.slice(0, 200),
    size: data.byteLength,
    width: w,
    height: h,
    hash: await sha256Hex(data),
    createdAt: Date.now(),
    data,
  };
}

/**
 * Simple background remover: flood-fills from the image border, removing pixels close in colour to the
 * border colours. Works for photos on plain/studio backgrounds; it is not ML subject detection.
 */
export async function removeBackground(asset: Asset, tolerance = 38): Promise<Asset> {
  const bmp = await decode(new Blob([asset.data], { type: asset.mime }));
  const w = 'naturalWidth' in bmp ? bmp.naturalWidth : bmp.width;
  const h = 'naturalHeight' in bmp ? bmp.naturalHeight : bmp.height;
  const c = makeCanvas(w, h);
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new ImageImportError('Canvas is unavailable.');
  ctx.drawImage(bmp, 0, 0);
  const img = ctx.getImageData(0, 0, w, h);
  removeBackgroundPixels(img.data, w, h, tolerance);
  ctx.putImageData(img, 0, 0);
  const data = await canvasToBytes(c, 'image/png');
  return { ...asset, id: uid('a'), mime: 'image/png', size: data.byteLength, hash: await sha256Hex(data), createdAt: Date.now(), data, name: `${asset.name ?? 'image'} (cut out)` };
}

/** Pure pixel routine (exported for tests). Mutates `px` (RGBA). */
export function removeBackgroundPixels(px: Uint8ClampedArray, w: number, h: number, tolerance: number): number {
  const visited = new Uint8Array(w * h);
  const stack: number[] = [];
  const tol2 = tolerance * tolerance * 3;
  const seed = (x: number, y: number) => {
    const i = y * w + x;
    if (!visited[i]) {
      visited[i] = 1;
      stack.push(i);
    }
  };
  for (let x = 0; x < w; x++) {
    seed(x, 0);
    seed(x, h - 1);
  }
  for (let y = 0; y < h; y++) {
    seed(0, y);
    seed(w - 1, y);
  }
  let removed = 0;
  while (stack.length) {
    const i = stack.pop()!;
    const o = i * 4;
    const r = px[o];
    const g = px[o + 1];
    const b = px[o + 2];
    px[o + 3] = 0;
    removed++;
    const x = i % w;
    const y = (i - x) / w;
    const neighbours = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
    for (const n of neighbours) {
      if (n < 0 || visited[n]) continue;
      const p = n * 4;
      const dr = px[p] - r;
      const dg = px[p + 1] - g;
      const db = px[p + 2] - b;
      if (dr * dr + dg * dg + db * db <= tol2 && px[p + 3] > 0) {
        visited[n] = 1;
        stack.push(n);
      }
    }
  }
  // Soften the cut edge: half-transparent pixels next to removed ones.
  for (let i = 0; i < w * h; i++) {
    if (visited[i]) continue;
    const x = i % w;
    const touching = (x > 0 && visited[i - 1]) || (x < w - 1 && visited[i + 1]) || (i >= w && visited[i - w]) || (i < w * (h - 1) && visited[i + w]);
    if (touching) px[i * 4 + 3] = Math.min(px[i * 4 + 3], 170);
  }
  return removed;
}

const urlCache = new Map<string, string>();

export function assetUrl(asset: Pick<Asset, 'id' | 'mime' | 'data'>): string {
  let url = urlCache.get(asset.id);
  if (!url) {
    url = URL.createObjectURL(new Blob([asset.data], { type: asset.mime }));
    urlCache.set(asset.id, url);
  }
  return url;
}

export function forgetAssetUrl(id: string): void {
  const url = urlCache.get(id);
  if (url) URL.revokeObjectURL(url);
  urlCache.delete(id);
}
